import { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TableSortLabel,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import PageTitle from '../../common/PageTitle';
import {
  octopusSyncFeatures,
} from "../../../data/featureRequests";

const infoText = (
  <>
    We value your ideas.
    <br />
    <br />
    We welcome your feature suggestions for OctopusSync. Everyone has the opportunity to propose new functionalities or improvements. To submit a request, please contact us exclusively via email at{' '}
    <span style={{ color: '#6a1b9a', fontWeight: 700 }}>support@octopussync.com</span>.
    Our team will carefully review each proposal and evaluate its potential. If approved, the feature will be added to our internal roadmap, documented, and implemented in a future update.
  </>
);

const tableColumns = [
  { id: "date", label: "Date" },
  { id: "feature", label: "Feature" },
  { id: "description", label: "Description" },
  { id: "beta", label: "Beta" },
  { id: "release", label: "Release" },
];

function FeatureTable({ features, onDescriptionClick }) {
  const [orderBy, setOrderBy] = useState("date");
  const [order, setOrder] = useState("desc");

  const handleSort = (colId) => {
    if (orderBy === colId) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(colId);
      setOrder("desc");
    }
  };

  const sortedRows = [...features].sort((a, b) => {
    const aVal = a[orderBy] || "";
    const bVal = b[orderBy] || "";
    if (order === "asc") {
      return aVal.localeCompare(bVal);
    } else {
      return bVal.localeCompare(aVal);
    }
  });

  return (
    <TableContainer
      component={Paper}
      sx={{ mb: 4, boxShadow: 3, borderRadius: 3, maxHeight: 600, overflowY: sortedRows.length > 10 ? 'auto' : 'visible' }}
    >
      <Table sx={{ minWidth: 650, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '110px', minWidth: '110px' }} /> {/* Date column fixed width */}
          <col style={{ width: '140px', minWidth: '140px' }} /> {/* Feature column fixed width */}
          <col style={{ width: 'auto' }} /> {/* Description column flexible */}
          <col style={{ width: '130px', minWidth: '130px' }} /> {/* Beta column wider for full date */}
          <col style={{ width: '130px', minWidth: '130px' }} /> {/* Release column wider for full date */}
        </colgroup>
        <TableHead>
          <TableRow>
            {tableColumns.map((col) => (
              <TableCell
                key={col.id}
                sx={{
                  fontWeight: 700,
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                  background: "linear-gradient(90deg, #ede7f6 0%, #f3e5f5 100%)",
                  borderBottom: '2px solid #d1c4e9',
                  whiteSpace: col.id === 'date' || col.id === 'beta' || col.id === 'release' ? 'nowrap' : undefined,
                  maxWidth: col.id === 'description' ? '1px' : undefined,
                  overflow: col.id === 'description' ? 'hidden' : undefined,
                  textOverflow: col.id === 'description' ? 'ellipsis' : undefined,
                }}
              >
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : "asc"}
                  onClick={() => handleSort(col.id)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, idx) => (
            <motion.tr
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.04 }}
              style={{
                background: idx % 2 === 0 ? "#fff" : "#f5f5f5",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#ececec")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  idx % 2 === 0 ? "#fff" : "#f5f5f5")
              }
            >
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {row.date || <span style={{ color: "#aaa" }}>—</span>}
              </TableCell>
              <TableCell>
                {row.feature || <span style={{ color: "#aaa" }}>—</span>}
              </TableCell>
              <TableCell
                sx={{
                  maxWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <motion.span
                    whileHover={{ scale: 1.08 }}
                    style={{
                      cursor: "pointer",
                      color: "#6a1b9a",
                      fontWeight: 500,
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                    onClick={() => onDescriptionClick(row)}
                    title={row.description}
                  >
                    {row.description && row.description.length > 60
                      ? row.description.slice(0, 57) + '...'
                      : row.description}
                  </motion.span>
                </Box>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                {row.beta || <span style={{ color: "#aaa" }}>—</span>}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                {row.release || <span style={{ color: "#aaa" }}>—</span>}
              </TableCell>
            </motion.tr>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const FeatureRequestPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFeature, setModalFeature] = useState(null);

  const handleDescriptionClick = (feature) => {
    setModalFeature(feature);
    setModalOpen(true);
  };
  const handleClose = () => setModalOpen(false);

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        backgroundColor: '#fff',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, md: 8 },
        width: '100%'
      }}
    >
      <Box sx={{ width: '100%' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 'xl', mx: 'auto' }}>
            <PageTitle title="Feature Requests" />
            <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: 700, mx: 'auto', lineHeight: 1.8 }}>
              {infoText}
            </Typography>
            <FeatureTable features={octopusSyncFeatures} onDescriptionClick={handleDescriptionClick} />
          </Box>
        </motion.div>
      </Box>
      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {modalFeature?.feature}
          <IconButton onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {modalFeature?.details}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FeatureRequestPage;
